import type {
  BuilderFieldOption,
  DashboardDateRangeMode,
  DashboardDefinition,
  DashboardKpiCard,
  DashboardMeasureParameter,
  DashboardMeasureParameterOption,
  DashboardSlicerPanel,
  DashboardTableColumn,
  DashboardWidget,
  DashboardWidgetFilter,
  DaxGenerationRequest,
} from './types'

// Coerces dashboardJson content into the shapes the DashboardDefinition /
// DashboardWidget types declare. The agent generates widget.dimensions,
// widget.measures, widget.columns as raw string arrays; this module lifts them
// into BuilderFieldOption / DashboardTableColumn objects so downstream code can
// trust the declared types without re-checking each entry.

function extractBracketLabel(value: string): string {
  const match = value.match(/\[([^\]]+)\]/)
  return match?.[1] ?? value
}

function isObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

function asString(value: unknown, fallback = ''): string {
  return typeof value === 'string' ? value : fallback
}

const DATE_TOKENS = new Set(['date', 'year', 'month', 'week', 'period', 'quarter', 'day', 'fy'])

// Split a label like "FiscalYearStart" or "As Of Date" or "Date[Date]" into
// its constituent words, lowercased. camelCase, spaces, punctuation, and
// brackets are all treated as separators so we can whole-word match against
// DATE_TOKENS below instead of relying on substring matches (which would
// misclassify e.g. "Candidate" as a date because it ends in "date").
function tokenizeLabel(piece: string): string[] {
  return piece
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .toLowerCase()
    .split(/[^a-z0-9]+/)
    .filter(Boolean)
}

// Raw string dimensions from agent-generated dashboards carry no explicit
// type, and defaulting them all to 'string' would misclassify date fields
// (e.g. 'Date'[Date]) for any downstream check that keys off type. Infer
// 'date' when the bracket label or field name contains a whole-word date
// token (Date, Year, Month, Week, Period, Quarter, Day, FY).
function inferFieldType(value: string, name: string): string {
  const bracket = extractBracketLabel(value)
  const candidates = bracket === value ? [name] : [bracket, name]
  return candidates.some((piece) => tokenizeLabel(piece).some((token) => DATE_TOKENS.has(token)))
    ? 'date'
    : 'string'
}

export function coerceFieldEntry(entry: unknown): BuilderFieldOption | null {
  if (typeof entry === 'string') {
    const trimmed = entry.trim()
    if (!trimmed) return null
    const name = extractBracketLabel(trimmed)
    return {
      value: trimmed,
      name,
      type: inferFieldType(trimmed, name),
    }
  }
  if (isObject(entry)) {
    const value = asString(entry['value']).trim() || asString(entry['name']).trim()
    if (!value) return null
    const name = asString(entry['name']).trim() || extractBracketLabel(value)
    const declaredType = asString(entry['type']).trim()
    const type = declaredType || inferFieldType(value, name)
    const result: BuilderFieldOption = { value, name, type }
    if (typeof entry['isCustom'] === 'boolean') result.isCustom = entry['isCustom']
    return result
  }
  return null
}

function coerceFieldEntries(value: unknown): BuilderFieldOption[] {
  if (!Array.isArray(value)) return []
  const out: BuilderFieldOption[] = []
  for (const entry of value) {
    const coerced = coerceFieldEntry(entry)
    if (coerced) out.push(coerced)
  }
  return out
}

export function coerceTableColumn(entry: unknown): DashboardTableColumn | null {
  if (typeof entry === 'string') {
    const trimmed = entry.trim()
    if (!trimmed) return null
    return { measure: trimmed }
  }
  if (isObject(entry)) {
    const measure = asString(entry['measure']).trim() || asString(entry['value']).trim()
    if (!measure) return null
    const col: DashboardTableColumn = { measure }
    if (typeof entry['label'] === 'string') col.label = entry['label']
    if (typeof entry['bar'] === 'boolean') col.bar = entry['bar']
    if (typeof entry['hidden'] === 'boolean') col.hidden = entry['hidden']
    if (isObject(entry['format'])) col.format = entry['format'] as DashboardTableColumn['format']
    if (Array.isArray(entry['conditionalFormat'])) {
      col.conditionalFormat = entry['conditionalFormat'] as DashboardTableColumn['conditionalFormat']
    }
    return col
  }
  return null
}

function coerceTableColumns(value: unknown): DashboardTableColumn[] | undefined {
  if (!Array.isArray(value)) return undefined
  const out: DashboardTableColumn[] = []
  for (const entry of value) {
    const coerced = coerceTableColumn(entry)
    if (coerced) out.push(coerced)
  }
  return out
}

function coerceKpiCard(entry: unknown): DashboardKpiCard | null {
  if (typeof entry === 'string') {
    const trimmed = entry.trim()
    if (!trimmed) return null
    return { measure: trimmed }
  }
  if (isObject(entry)) {
    const measure = asString(entry['measure']).trim() || asString(entry['value']).trim()
    if (!measure) return null
    const card: DashboardKpiCard = { measure }
    if (typeof entry['label'] === 'string') card.label = entry['label']
    if (typeof entry['lead'] === 'boolean') card.lead = entry['lead']
    if (typeof entry['sub'] === 'string') card.sub = entry['sub']
    if (typeof entry['hidden'] === 'boolean') card.hidden = entry['hidden']
    if (isObject(entry['format'])) card.format = entry['format'] as DashboardKpiCard['format']
    if (Array.isArray(entry['conditionalFormat'])) {
      card.conditionalFormat = entry['conditionalFormat'] as DashboardKpiCard['conditionalFormat']
    }
    return card
  }
  return null
}

function coerceKpiCards(value: unknown): DashboardKpiCard[] | undefined {
  if (!Array.isArray(value)) return undefined
  const out: DashboardKpiCard[] = []
  for (const entry of value) {
    const coerced = coerceKpiCard(entry)
    if (coerced) out.push(coerced)
  }
  return out
}

function coerceFilters(value: unknown): DashboardWidgetFilter[] {
  if (!Array.isArray(value)) return []
  const out: DashboardWidgetFilter[] = []
  for (const entry of value) {
    if (!isObject(entry)) continue
    const fieldName = asString(entry['fieldName']).trim()
    const fieldValue = asString(entry['fieldValue']).trim()
    if (!fieldName && !fieldValue) continue
    out.push({
      fieldName: fieldName || fieldValue,
      fieldValue: fieldValue || fieldName,
      type: asString(entry['type']).trim() || 'string',
      operator: asString(entry['operator']).trim() || '=',
      value: (entry['value'] as DashboardWidgetFilter['value']) ?? null,
    })
  }
  return out
}

function coerceStringArray(value: unknown): string[] {
  if (!Array.isArray(value)) return []
  const out: string[] = []
  for (const entry of value) {
    if (typeof entry === 'string' && entry.trim()) out.push(entry.trim())
  }
  return out
}

function coerceDaxGenerationRequest(value: unknown): DaxGenerationRequest {
  const source = isObject(value) ? value : {}
  return {
    dimensions: coerceStringArray(source['dimensions']),
    measures: coerceStringArray(source['measures']),
    filters: Array.isArray(source['filters'])
      ? (source['filters'].filter(isObject) as DaxGenerationRequest['filters'])
      : [],
  }
}

function coerceMeasureParameters(value: unknown): DashboardMeasureParameter[] | undefined {
  if (!Array.isArray(value)) return undefined
  const out: DashboardMeasureParameter[] = []
  for (const entry of value) {
    if (!isObject(entry)) continue
    const id = asString(entry['id']).trim()
    if (!id) continue
    const options: DashboardMeasureParameterOption[] = []
    for (const option of Array.isArray(entry['options']) ? entry['options'] : []) {
      if (!isObject(option)) continue
      const optionId = asString(option['id']).trim()
      const measure = asString(option['measure']).trim()
      if (!optionId || !measure) continue
      options.push({ id: optionId, label: asString(option['label']) || measure, measure })
    }
    if (options.length === 0) continue
    const parameter: DashboardMeasureParameter = { id, label: asString(entry['label']), options }
    if (typeof entry['defaultOptionId'] === 'string') parameter.defaultOptionId = entry['defaultOptionId']
    out.push(parameter)
  }
  return out.length ? out : undefined
}

function coerceWidget(value: unknown): DashboardWidget | null {
  if (!isObject(value)) return null
  const id = asString(value['id']).trim()
  const type = asString(value['type']).trim() as DashboardWidget['type']
  if (!id || !type) return null

  const widget: DashboardWidget = {
    id,
    type,
    title: asString(value['title']),
    description: asString(value['description']),
    dimensions: coerceFieldEntries(value['dimensions']),
    measures: coerceFieldEntries(value['measures']),
    filters: coerceFilters(value['filters']),
    daxGenerationRequest: coerceDaxGenerationRequest(value['daxGenerationRequest']),
  }

  if (typeof value['size'] === 'string') widget.size = value['size'] as DashboardWidget['size']
  if (typeof value['width'] === 'number') widget.width = value['width']
  if (typeof value['height'] === 'number') widget.height = value['height']
  if (typeof value['showTitle'] === 'boolean') widget.showTitle = value['showTitle']
  if (Array.isArray(value['validation'])) widget.validation = value['validation'].filter((v): v is string => typeof v === 'string')
  if (Array.isArray(value['availableTypes'])) {
    widget.availableTypes = value['availableTypes'].filter((v): v is DashboardWidget['type'] => typeof v === 'string') as DashboardWidget['availableTypes']
  }
  const cards = coerceKpiCards(value['cards'])
  if (cards) widget.cards = cards
  const columns = coerceTableColumns(value['columns'])
  if (columns) widget.columns = columns
  if (typeof value['rowDimension'] === 'string') widget.rowDimension = value['rowDimension']
  if (Array.isArray(value['drillDimensions'])) widget.drillDimensions = coerceStringArray(value['drillDimensions'])
  if (Array.isArray(value['drillDownDimensions'])) widget.drillDownDimensions = coerceStringArray(value['drillDownDimensions'])
  if (Array.isArray(value['rowFields'])) widget.rowFields = coerceStringArray(value['rowFields'])
  if (Array.isArray(value['columnFields'])) widget.columnFields = coerceStringArray(value['columnFields'])
  if (Array.isArray(value['valueMeasures'])) widget.valueMeasures = coerceStringArray(value['valueMeasures'])
  if (Array.isArray(value['hiddenMeasures'])) widget.hiddenMeasures = coerceStringArray(value['hiddenMeasures'])
  if (Array.isArray(value['conditionalFormat'])) {
    widget.conditionalFormat = value['conditionalFormat'] as DashboardWidget['conditionalFormat']
  }
  if (typeof value['showTotals'] === 'boolean') widget.showTotals = value['showTotals']
  if (typeof value['showBars'] === 'boolean') widget.showBars = value['showBars']
  if (typeof value['barScale'] === 'string') widget.barScale = value['barScale'] as DashboardWidget['barScale']
  if (typeof value['zebra'] === 'boolean') widget.zebra = value['zebra']
  if (typeof value['fixedHeight'] === 'boolean') widget.fixedHeight = value['fixedHeight']
  if (typeof value['trendGranularity'] === 'string') widget.trendGranularity = value['trendGranularity'] as DashboardWidget['trendGranularity']
  if (typeof value['comparePreviousYear'] === 'boolean') widget.comparePreviousYear = value['comparePreviousYear']
  if (typeof value['actualMeasure'] === 'string') widget.actualMeasure = value['actualMeasure']
  if (typeof value['targetMeasure'] === 'string') widget.targetMeasure = value['targetMeasure']
  if (typeof value['allowMultiFieldSelection'] === 'boolean') widget.allowMultiFieldSelection = value['allowMultiFieldSelection']
  const orientation = value['orientation']
  if ((widget.type === 'bar' || widget.type === 'groupedBar') && (orientation === 'horizontal' || orientation === 'vertical')) {
    widget.orientation = orientation
  }
  const measureParameters = coerceMeasureParameters(value['measureParameters'])
  if (measureParameters) widget.measureParameters = measureParameters

  return widget
}

// Dashboards saved before the Power BI date presets carry a fixed preset token
// (slicerPanel.dateRange.defaultPreset, or the panel-level defaultDatePreset)
// instead of dateRange.mode. Tokens with a mode equivalent keep their meaning;
// any other relative period has no stable catalog counterpart, so it is left
// without a mode and opens on the catalog's default preset.
function coerceLegacyDateRangeMode(token: unknown): DashboardDateRangeMode | undefined {
  if (token === 'custom' || token === 'explicitRange') return 'custom'
  if (token === 'noDateFilter') return 'none'
  return undefined
}

function coerceSlicerPanel(value: unknown): DashboardDefinition['slicerPanel'] {
  if (!isObject(value)) return value as DashboardDefinition['slicerPanel']
  const { defaultDatePreset, ...panel } = value
  if (!isObject(panel['dateRange'])) return panel as DashboardSlicerPanel

  const { defaultPreset, ...dateRange } = panel['dateRange']
  if (dateRange['mode'] === undefined) {
    const mode = coerceLegacyDateRangeMode(defaultPreset ?? defaultDatePreset)
    if (mode) dateRange['mode'] = mode
  }
  return { ...panel, dateRange } as DashboardSlicerPanel
}

export function coerceDashboardDefinition(value: unknown): DashboardDefinition | null {
  if (!isObject(value)) return null
  const widgets: DashboardWidget[] = []
  if (Array.isArray(value['widgets'])) {
    for (const raw of value['widgets']) {
      const widget = coerceWidget(raw)
      if (widget) widgets.push(widget)
    }
  }

  const dashboard: DashboardDefinition = {
    version: asString(value['version']),
    intentKey: asString(value['intentKey']),
    platformType: asString(value['platformType']),
    platformTypeId: typeof value['platformTypeId'] === 'number' ? value['platformTypeId'] : Number(value['platformTypeId']) || 0,
    title: asString(value['title']),
    summary: asString(value['summary']),
    widgets,
    warnings: Array.isArray(value['warnings']) ? value['warnings'].filter((v): v is string => typeof v === 'string') : [],
  }

  if (typeof value['rootId'] === 'string') dashboard.rootId = value['rootId']
  if (typeof value['rootDashboardId'] === 'string') dashboard.rootDashboardId = value['rootDashboardId']
  if (typeof value['dashboardId'] === 'string') dashboard.dashboardId = value['dashboardId']
  if (typeof value['clientId'] === 'string') dashboard.clientId = value['clientId']
  if (value['section'] !== undefined) dashboard.section = value['section'] as DashboardDefinition['section']
  if (Array.isArray(value['dashboardFilters'])) {
    dashboard.dashboardFilters = value['dashboardFilters'].filter(isObject) as DashboardDefinition['dashboardFilters']
  }
  if (value['slicerPanel'] !== undefined) dashboard.slicerPanel = coerceSlicerPanel(value['slicerPanel'])

  return dashboard
}

export function parseDashboardDefinitionJson(json: string | null | undefined): DashboardDefinition | null {
  if (!json) return null
  let parsed: unknown
  try {
    parsed = JSON.parse(json)
  } catch {
    return null
  }
  return coerceDashboardDefinition(parsed)
}
